import React, { useState, useMemo, useEffect } from "react";
import {
  Box, Typography, CircularProgress, Chip, Avatar,
  TextField, InputAdornment, Collapse, IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import WcOutlinedIcon from "@mui/icons-material/WcOutlined";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import { getCustomer } from "../../service/customer";

const appColor = (name) => {
  const n = name?.toLowerCase() || "";
  if (n === "musicccare")   return { bg: "#dcfce7", color: "#166534", border: "#86efac", subBg: "#f0fdf4" };
  if (n === "echobuddy")    return { bg: "#f3e8ff", color: "#7e22ce", border: "#d8b4fe", subBg: "#faf5ff" };
  if (n === "echoshine")    return { bg: "#eff6ff", color: "#2670d8", border: "#bfdbfe", subBg: "#f0f9ff" };
  if (n === "echomindnest") return { bg: "#fef9c3", color: "#854d0e", border: "#fde047", subBg: "#fefce8" };
  return { bg: "#f1f5f9", color: "#475569", border: "#cbd5e1", subBg: "#f8fafc" };
};

const avatarPalette = (id) => {
  const palettes = [
    { bg: "#d1fae5", color: "#065f46" },
    { bg: "#dbeafe", color: "#1e40af" },
    { bg: "#fef3c7", color: "#92400e" },
    { bg: "#fce7f3", color: "#9d174d" },
    { bg: "#ede9fe", color: "#5b21b6" },
  ];
  return palettes[(id || 0) % palettes.length];
};

const customerCache = {};

const AptRow = ({ apt, formatDate, formatTime, getStatusStyles, highlight }) => {
  const appC = appColor(apt.customer_app_name);
  const now = new Date();

  const sessionStartDt = apt.slot_date && apt.start_time
    ? new Date(`${apt.slot_date.split("T")[0]}T${apt.start_time}`)
    : null;
  const sessionEndDt = apt.slot_date && apt.end_time
    ? new Date(`${apt.slot_date.split("T")[0]}T${apt.end_time}`)
    : null;
  const aptSessionEnded = sessionEndDt && now >= sessionEndDt;
  const hoursAfterSession = sessionEndDt ? (now - sessionEndDt) / (1000 * 60 * 60) : null;
  const isDraftedOver72h = apt.status?.toLowerCase() === "drafted" && hoursAfterSession !== null && hoursAfterSession >= 72;
  const isDraftedUnder72h = apt.status?.toLowerCase() === "drafted" && hoursAfterSession !== null && hoursAfterSession < 72;

  const hasMeetingActivity = !!(apt.customer_state?.trim() || apt.counsellor_state?.trim());
  const bothInSession = apt.customer_state === "in-session" && apt.counsellor_state === "in-session";
  const bothHaveActivity = !!(apt.customer_state?.trim() && apt.counsellor_state?.trim());
  const isMeetingInProgress = apt.status === "scheduled" && bothInSession;
  const isPendingPatientUpdate = apt.status === "scheduled" && bothHaveActivity && !bothInSession;
  const isNotAttendedBlank = apt.status === "scheduled" && !hasMeetingActivity && aptSessionEnded;
  const isCustomerNotAttended = apt.status === "scheduled" && !apt.customer_state?.trim() && !!apt.counsellor_state?.trim();
  const isCounsellorNotAttended = apt.status === "scheduled" && !!apt.customer_state?.trim() && !apt.counsellor_state?.trim();

  const resolveParticipantStatus = (state) => {
    if (aptSessionEnded && state?.toLowerCase() === 'in-session') return 'ended';
    if (state && state.trim() !== "" && state.toLowerCase() !== "scheduled") return state;
    if (aptSessionEnded) return "No Show";
    return state || "—";
  };

  const customerDisplay  = resolveParticipantStatus(apt.customer_state);
  const counsellorDisplay = resolveParticipantStatus(apt.counsellor_state);
  const isNoShowCustomer   = customerDisplay === "No Show";
  const isNoShowCounsellor = counsellorDisplay === "No Show";

  const isCancelled = apt.status?.toLowerCase() === "cancelled" || apt.status?.toLowerCase() === "canceled";

  return (
  <Box sx={{
    px: 2, py: 1.5,
    display: "flex", flexDirection: "column", gap: 0.75,
    bgcolor: highlight ? "#eff6ff" : "#f8fafc",
    borderRadius: "8px",
    border: `1px solid ${highlight ? "#bfdbfe" : "#e2e8f0"}`,
  }}>
    {/* Row A: Appt ID · Slot · Type */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.4 }}>
        <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Appt ID</Typography>
        <Typography sx={{ fontSize: "0.76rem", fontWeight: 700, color: "#6d28d9" }}>#{apt.id}</Typography>
      </Box>

      <Box sx={{ width: "1px", height: 12, bgcolor: "#e2e8f0" }} />

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
        <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Slot</Typography>
        <CalendarTodayOutlinedIcon sx={{ fontSize: "0.68rem", color: "#64748b" }} />
        <Typography sx={{ fontSize: "0.76rem", fontWeight: 500, color: "#334155" }}>{formatDate(apt.slot_date)}</Typography>
        <AccessTimeOutlinedIcon sx={{ fontSize: "0.68rem", color: "#64748b" }} />
        <Typography sx={{ fontSize: "0.76rem", fontWeight: 500, color: "#334155" }}>{formatTime(apt.start_time)} – {formatTime(apt.end_time)}</Typography>
      </Box>

      {apt.consultation_type && (
        <>
          <Box sx={{ width: "1px", height: 12, bgcolor: "#e2e8f0" }} />
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.4 }}>
            <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Type</Typography>
            <Typography sx={{ fontSize: "0.76rem", fontWeight: 600, color: apt.consultation_type?.toLowerCase() === "video" ? "#4f46e5" : "#0891b2" }}>
              {apt.consultation_type.replace(/\b\w/g, (c) => c.toUpperCase())}
            </Typography>
          </Box>
        </>
      )}
    </Box>

    {/* Row B: App · Account · Status */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
      {apt.customer_app_name && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
          <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>App</Typography>
          <Box sx={{ px: 0.75, py: 0.1, bgcolor: appC.bg, borderRadius: "4px", border: `1px solid ${appC.border}` }}>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: appC.color, lineHeight: 1.6 }}>{apt.customer_app_name}</Typography>
          </Box>
        </Box>
      )}

      {apt.customer_account_name && apt.customer_account_name !== apt.customer_app_name && (
        <>
          <Box sx={{ width: "1px", height: 12, bgcolor: "#e2e8f0" }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Account</Typography>
            <Box sx={{ px: 0.75, py: 0.1, bgcolor: appC.subBg, borderRadius: "4px", border: `1px solid ${appC.border}` }}>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 500, color: appC.color, lineHeight: 1.6 }}>{apt.customer_account_name}</Typography>
            </Box>
          </Box>
        </>
      )}

      {(apt.customer_app_name || apt.customer_account_name) && <Box sx={{ width: "1px", height: 12, bgcolor: "#e2e8f0" }} />}

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
        <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>status</Typography>
        <Box sx={{
          px: 0.75, py: 0.1, borderRadius: "4px", border: "1px solid",
          ...(isMeetingInProgress
            ? { bgcolor: "#dcfce7", borderColor: "#86efac", color: "#166534" }
            : isPendingPatientUpdate || isDraftedUnder72h
            ? { bgcolor: "#fef9c3", borderColor: "#fde047", color: "#854d0e" }
            : isDraftedOver72h
            ? { bgcolor: "#fee2e2", borderColor: "#fca5a5", color: "#b91c1c" }
            : (isNotAttendedBlank || isCustomerNotAttended || isCounsellorNotAttended)
            ? { bgcolor: "#fee2e2", borderColor: "#fca5a5", color: "#b91c1c" }
            : getStatusStyles(apt.status)),
        }}>
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, lineHeight: 1.6,
            color: isMeetingInProgress ? "#166534" : (isPendingPatientUpdate || isDraftedUnder72h) ? "#854d0e" : (isDraftedOver72h || isNotAttendedBlank || isCustomerNotAttended || isCounsellorNotAttended) ? "#b91c1c" : "inherit"
          }}>
            {isMeetingInProgress
              ? "Meeting In Progress"
              : isPendingPatientUpdate
              ? "Pending Patient Record Update"
              : isNotAttendedBlank
              ? "No Show"
              : isCustomerNotAttended
              ? "Customer No Show"
              : isCounsellorNotAttended
              ? "Counsellor No Show"
              : isDraftedUnder72h
              ? "Pending Patient Record Update"
              : isDraftedOver72h
              ? "Patient Record Not Updated"
              : apt.status?.replace(/\b\w/g, (c) => c.toUpperCase())}
          </Typography>
        </Box>
      </Box>
    </Box>

    {/* Row C: Customer State · Counsellor State — skip for cancelled appointments */}
    {!isCancelled && (apt.customer_state?.trim() || apt.counsellor_state?.trim() || aptSessionEnded) && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Meeting Status</Typography>
        <Box sx={{ width: "1px", height: 12, bgcolor: "#e2e8f0" }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
          <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Customer</Typography>
          <Box sx={{ px: 0.75, py: 0.1, borderRadius: "4px", border: "1px solid",
            bgcolor: isNoShowCustomer ? "#fee2e2" : "#f1f5f9",
            borderColor: isNoShowCustomer ? "#fca5a5" : "#e2e8f0",
          }}>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, lineHeight: 1.6, color: isNoShowCustomer ? "#b91c1c" : "#334155" }}>
              {customerDisplay}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ width: "1px", height: 12, bgcolor: "#e2e8f0" }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
          <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Counsellor</Typography>
          <Box sx={{ px: 0.75, py: 0.1, borderRadius: "4px", border: "1px solid",
            bgcolor: isNoShowCounsellor ? "#fee2e2" : "#f1f5f9",
            borderColor: isNoShowCounsellor ? "#fca5a5" : "#e2e8f0",
          }}>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, lineHeight: 1.6, color: isNoShowCounsellor ? "#b91c1c" : "#334155" }}>
              {counsellorDisplay}
            </Typography>
          </Box>
        </Box>
      </Box>
    )}

    {/* Row D: Cancellation info */}
    {isCancelled && (
      <Box sx={{ bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "6px", px: 1.25, py: 0.875, display: "flex", flexDirection: "column", gap: 0.35 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          {apt.cancellation_triggered_by ? (
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
              <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>Cancelled by</Typography>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#b91c1c" }}>
                {apt.cancellation_triggered_by.replace(/\b\w/g, (c) => c.toUpperCase())}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#b91c1c", fontStyle: "italic" }}>
              Cancellation details not recorded
            </Typography>
          )}
          {apt.cancelled_at && (
            <>
              <Box sx={{ width: "1px", height: 12, bgcolor: "#fca5a5" }} />
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>Cancelled at</Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#b91c1c" }}>
                  {new Date(apt.cancelled_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" })}
                </Typography>
              </Box>
            </>
          )}
        </Box>
        {apt.cancellation_remark ? (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
            <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", mt: 0.15 }}>Remark</Typography>
            <Typography sx={{ fontSize: "0.72rem", color: "#374151", lineHeight: 1.5 }}>{apt.cancellation_remark}</Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", fontStyle: "italic" }}>
            No cancellation remark provided
          </Typography>
        )}
      </Box>
    )}

  </Box>
  );
};

const PatientRow = ({ patient, getPatientInfo, handleAppointmentClick, formatDate, formatTime, getStatusStyles, today }) => {
  const [expanded, setExpanded] = useState(false);
  const [customerData, setCustomerData] = useState(customerCache[patient.customer_id] || null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!expanded || customerData) return;
    if (customerCache[patient.customer_id]) { setCustomerData(customerCache[patient.customer_id]); return; }
    setFetching(true);
    getCustomer(patient.customer_id)
      .then((d) => { if (d?.id) { customerCache[patient.customer_id] = d; setCustomerData(d); } })
      .finally(() => setFetching(false));
  }, [expanded]);

  const patientInfo = getPatientInfo(patient.appointments[0]);
  const latestApt   = patient.appointments[0];
  const isNew       = latestApt.is_new_case;
  const pal         = avatarPalette(patient.customer_id);
  const appC        = appColor(latestApt.customer_app_name);

  const todayApts = patient.appointments.filter((a) => new Date(a.slot_date).toISOString().split("T")[0] === today);
  const pastApts  = patient.appointments.filter((a) => new Date(a.slot_date).toISOString().split("T")[0] < today);

  const completedCount = patient.appointments.filter((a) => a.status === "completed").length;
  const lastCompleted  = patient.appointments.find((a) => a.status === "completed");
  const lastVisit = lastCompleted
    ? new Date(lastCompleted.slot_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : null;

  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
      {/* Header row */}
      <Box
        onClick={() => setExpanded((p) => !p)}
        sx={{ px: 2.5, py: 1.75, display: "flex", alignItems: "center", gap: 2, cursor: "pointer", "&:hover": { bgcolor: "#fafafa" } }}
      >
        <Avatar sx={{ width: 42, height: 42, bgcolor: pal.bg, color: pal.color, fontWeight: 700, fontSize: "0.9rem", flexShrink: 0 }}>
          {patientInfo.initials}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.9375rem", color: "#111827" }}>{patientInfo.name}</Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af", fontFamily: "monospace" }}>#{patientInfo.id}</Typography>
            <Chip label={isNew ? "NEW" : "RETURNING"} size="small" sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, bgcolor: isNew ? "#fff7ed" : "#f0fdf4", color: isNew ? "#c2410c" : "#15803d", border: `1px solid ${isNew ? "#fed7aa" : "#bbf7d0"}`, "& .MuiChip-label": { px: 0.875 } }} />
            {latestApt.customer_app_name && (
              <Box sx={{ display: "inline-flex", alignItems: "center", height: 20, borderRadius: "6px", overflow: "hidden", border: `1px solid ${appC.border}` }}>
                {/* App segment */}
                <Box sx={{ px: 0.875, height: "100%", display: "flex", alignItems: "center", bgcolor: appC.bg, fontSize: "0.65rem", fontWeight: 600, color: appC.color, whiteSpace: "nowrap" }}>
                  {latestApt.customer_app_name}
                </Box>
                {/* Account sub-segment — only when different */}
                {latestApt.customer_account_name && latestApt.customer_account_name !== latestApt.customer_app_name && (
                  <>
                    <Box sx={{ width: "1px", height: "60%", bgcolor: appC.border }} />
                    <Box sx={{ px: 0.875, height: "100%", display: "flex", alignItems: "center", bgcolor: appC.subBg, fontSize: "0.63rem", fontWeight: 500, color: appC.color, whiteSpace: "nowrap" }}>
                      {latestApt.customer_account_name}
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.4, flexWrap: "wrap" }}>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
              <CalendarTodayOutlinedIcon sx={{ fontSize: "0.72rem", color: "#9ca3af" }} />
              <Typography sx={{ fontSize: "0.72rem", color: "#6b7280" }}>{patient.appointments.length} appt{patient.appointments.length !== 1 ? "s" : ""}</Typography>
            </Box>
            {completedCount > 0 && (
              <Typography sx={{ fontSize: "0.72rem", color: "#15803d" }}>{completedCount} completed</Typography>
            )}
            {lastVisit && (
              <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af" }}>Last visit: {lastVisit}</Typography>
            )}
          </Box>
        </Box>

        {todayApts.length > 0 && (
          <Chip label={`Today: ${todayApts.length}`} size="small" sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700, bgcolor: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe", "& .MuiChip-label": { px: 1 } }} />
        )}
        <IconButton size="small" sx={{ color: "#9ca3af", flexShrink: 0 }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Expanded panel */}
      <Collapse in={expanded}>
        <Box sx={{ borderTop: "1px solid #f1f5f9" }}>
          {/* Demographics strip */}
          <Box sx={{ px: 2.5, py: 1.25, bgcolor: "#f8fafc", display: "flex", gap: 3, flexWrap: "wrap", alignItems: "center", borderBottom: "1px solid #f1f5f9" }}>
            {fetching ? (
              <CircularProgress size={14} sx={{ color: "#9ca3af" }} />
            ) : customerData ? (
              <>
                {(customerData.age || customerData.dob) && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CakeOutlinedIcon sx={{ fontSize: "0.8rem", color: "#9ca3af" }} />
                    <Typography sx={{ fontSize: "0.75rem", color: "#374151" }}>
                      {customerData.age ? `${customerData.age} yrs` : new Date(customerData.dob).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </Typography>
                  </Box>
                )}
                {customerData.gender && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <WcOutlinedIcon sx={{ fontSize: "0.8rem", color: "#9ca3af" }} />
                    <Typography sx={{ fontSize: "0.75rem", color: "#374151" }}>
                      {customerData.gender.charAt(0).toUpperCase() + customerData.gender.slice(1)}
                    </Typography>
                  </Box>
                )}
                {customerData.marital_status && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <PersonOutlineIcon sx={{ fontSize: "0.8rem", color: "#9ca3af" }} />
                    <Typography sx={{ fontSize: "0.75rem", color: "#374151" }}>
                      {customerData.marital_status.charAt(0).toUpperCase() + customerData.marital_status.slice(1)}
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", fontStyle: "italic" }}>No demographic data on record</Typography>
            )}
          </Box>

          <Box sx={{ px: 2.5, pb: 2 }}>
            {/* Today's appointments */}
            {todayApts.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#2563eb", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.875 }}>
                  Today
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {todayApts.map((apt) => (
                    <AptRow key={apt.id} apt={apt} formatDate={formatDate} formatTime={formatTime} getStatusStyles={getStatusStyles} highlight />
                  ))}
                </Box>
              </Box>
            )}

            {/* Past appointments */}
            {pastApts.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                {todayApts.length > 0 && (
                  <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.875 }}>
                    Past
                  </Typography>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {pastApts.map((apt) => (
                    <AptRow key={apt.id} apt={apt} formatDate={formatDate} formatTime={formatTime} getStatusStyles={getStatusStyles} />
                  ))}
                </Box>
              </Box>
            )}

            {todayApts.length === 0 && pastApts.length === 0 && (
              <Typography sx={{ fontSize: "0.82rem", color: "#9ca3af", py: 2, textAlign: "center" }}>No appointments found</Typography>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

const PatientDetails = ({
  appointments, loading, today,
  getPatientInfo, handleAppointmentClick,
  formatDate, formatTime, getStatusStyles,
}) => {
  const [search, setSearch] = useState("");

  const allApts = useMemo(() =>
    appointments.filter((a) => {
      const d = new Date(a.slot_date).toISOString().split("T")[0];
      return d <= today;
    }),
  [appointments, today]);

  const filtered = useMemo(() => {
    if (!search.trim()) return allApts;
    const q = search.toLowerCase();
    return allApts.filter((a) =>
      a.customer_name?.toLowerCase().includes(q) ||
      String(a.customer_id || "").includes(q) ||
      a.customer_email?.toLowerCase().includes(q) ||
      a.consultation_type?.toLowerCase().includes(q)
    );
  }, [allApts, search]);

  const groupedPatients = useMemo(() => {
    const groups = {};
    filtered.forEach((apt) => {
      const id = apt.customer_id;
      if (!groups[id]) {
        groups[id] = { customer_id: id, customer_name: apt.customer_name, customer_email: apt.customer_email, appointments: [] };
      }
      groups[id].appointments.push(apt);
    });
    return Object.values(groups)
      .map((g) => ({ ...g, appointments: [...g.appointments].sort((a, b) => b.id - a.id) }))
      .sort((a, b) => b.appointments[0].id - a.appointments[0].id);
  }, [filtered]);

  const todayPatientCount = useMemo(() =>
    groupedPatients.filter((p) => p.appointments.some((a) => new Date(a.slot_date).toISOString().split("T")[0] === today)).length,
  [groupedPatients, today]);

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 1.5, sm: 2.5, md: 3 }, pb: 6 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2.5, flexWrap: "wrap", gap: 1.5 }}>
        <Box>
          <Typography sx={{ color: "#6b7280", fontSize: "0.875rem" }}>Manage patient records and consultation history</Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 0.75 }}>
            <Chip label={`${groupedPatients.length} patient${groupedPatients.length !== 1 ? "s" : ""}`} size="small" sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600, bgcolor: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0" }} />
            {todayPatientCount > 0 && (
              <Chip label={`${todayPatientCount} today`} size="small" sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600, bgcolor: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe" }} />
            )}
          </Box>
        </Box>
        <TextField
          variant="outlined" size="small"
          placeholder="Search by name, ID ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#9ca3af", fontSize: "1rem" }} /></InputAdornment>,
            sx: {
              borderRadius: "10px", bgcolor: "#fff", fontSize: "0.82rem",
              "& fieldset": { borderColor: "#e2e8f0" },
              "&:hover fieldset": { borderColor: "#cbd5e1" },
              "&.Mui-focused fieldset": { borderColor: "#6366f1", borderWidth: "1.5px" },
            },
          }}
          sx={{ width: 280 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#6366f1" }} />
        </Box>
      ) : groupedPatients.length === 0 ? (
        <Box sx={{ py: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <PersonOutlineIcon sx={{ fontSize: "2.5rem", color: "#d1d5db" }} />
          <Typography sx={{ fontSize: "0.875rem", color: "#6b7280" }}>
            {search.trim() ? "No patients found matching your search" : "No patient history found"}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {groupedPatients.map((patient) => (
            <PatientRow
              key={patient.customer_id}
              patient={patient}
              getPatientInfo={getPatientInfo}
              handleAppointmentClick={handleAppointmentClick}
              formatDate={formatDate}
              formatTime={formatTime}
              getStatusStyles={getStatusStyles}
              today={today}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PatientDetails;
